<script setup lang="ts">
  import { computed } from 'vue'
  import {
    ComboboxHeader,
    ComboboxHeaderButton,
    ComboboxHeaderTitle,
    InputGroupCombobox
  } from '@gat-ship/vue-components'
  import { IconCheck } from '@gat-ship/icons'
  import { useSelectCurrencyDataSource } from '../../composables/useSelectCurrencyDataSource'
  import { toSelectItems } from './to-select-items'
  import type { RefDataPickerEmits, RefDataPickerProps } from './picker-props'

  /**
   * A currency picker: owns its own data fetching and composes the generic
   * `InputGroupCombobox` primitive with currency chrome — dropdown rows show
   * the currency name + code.
   *
   * It brings no border of its own — drop it into a `FormField` default slot
   * (like `InputGroupInput`) and it picks up the parent `InputGroup`'s focus
   * / error chrome. Can also be used standalone with `v-model`.
   *
   * Usage:
   * ```vue
   * <FormField :form="form" name="currencyCode" label="Currency" required>
   *   <template #default="{ field, invalid }">
   *     <CurrencyPicker
   *       :id="field.name"
   *       :model-value="field.state.value"
   *       :aria-invalid="invalid"
   *       @update:model-value="field.handleChange"
   *       @blur="field.handleBlur"
   *     />
   *   </template>
   * </FormField>
   * ```
   */
  const props = defineProps<
    RefDataPickerProps & {
      /** Pre-add a currency so it shows even before the full list loads. */
      preSelectedCurrencyCode?: string
    }
  >()

  defineEmits<RefDataPickerEmits>()

  const { currencies, loadingCurrencies, fetchCurrencies, getCurrency } =
    useSelectCurrencyDataSource()

  // Lazy fetch on first render
  fetchCurrencies()

  // Pre-add a currency if provided
  if (props.preSelectedCurrencyCode) {
    getCurrency(props.preSelectedCurrencyCode)
  }

  const currencyItems = computed(() =>
    toSelectItems(currencies.value, {
      value: (c) => c.code,
      title: (c) => c.name,
      sort: (a, b) => a.name.localeCompare(b.name)
    })
  )
</script>

<template>
  <InputGroupCombobox
    :id="id"
    :items="currencyItems"
    :model-value="modelValue"
    :loading="loadingCurrencies"
    :placeholder="placeholder"
    :disabled="disabled"
    :aria-invalid="ariaInvalid"
    :data-testid="dataTestid"
    :input-class="inputClass"
    @update:model-value="$emit('update:modelValue', $event)"
    @blur="$emit('blur')"
  >
    <template #content-header>
      <ComboboxHeader>
        <ComboboxHeaderTitle>Currencies</ComboboxHeaderTitle>
        <ComboboxHeaderButton
          v-if="canManage"
          tabindex="-1"
          @click="$emit('manage')"
        >
          Manage
        </ComboboxHeaderButton>
      </ComboboxHeader>
    </template>
    <template #item="{ item, selected }">
      <span class="flex-1 truncate text-sm">{{ item.title }}</span>
      <span class="text-muted-foreground text-xs tabular-nums">
        {{ item.value }}
      </span>
      <IconCheck
        :class="[
          'ml-auto size-3.5 shrink-0',
          selected ? 'opacity-100' : 'opacity-0'
        ]"
      />
    </template>
  </InputGroupCombobox>
</template>
